import { z } from 'zod';
import { packCategorySchema, IPackCategory } from './packCategory';

/**
 * Schema for validating pack category form data.
 * Extends the base pack category schema with form-specific validations.
 *
 * @property {string} code - Pack category code (max 5 characters)
 * @property {string} category - Category name (max 255 characters)
 */
export const packCategoryFormSchema = packCategorySchema.extend({});

/**
 * Default form schema for new pack categories.
 * Makes all required fields optional with empty defaults.
 */
export const packCategoryDefaultFormSchema = packCategoryFormSchema.extend({
    code: z.string().max(5, 'Code can only be up to 5 characters long.').optional().default(''),
    category: z.string().max(255, 'Category can only be up to 255 characters long.').optional().default(''),
});

/**
 * TypeScript interface representing PackCategory form values.
 * Generated from the Zod schema for type safety.
 */
export type IPackCategoryFormValues = z.infer<typeof packCategoryFormSchema>;

/**
 * Factory function to create form values for a pack category.
 * Handles both new and existing pack categories.
 *
 * @param {IPackCategory} [packCategory] - Optional existing pack category to populate form
 * @returns {IPackCategoryFormValues} Form values with appropriate defaults
 */
export const PackCategoryFormValues = (packCategory ?: IPackCategory) : IPackCategoryFormValues => {
    // Handle the default case (new pack category)
    if (!packCategory) {
        return packCategoryDefaultFormSchema.parse({});
    }

    // Transform the IPackCategory data into IPackCategoryFormValues structure
    return packCategoryFormSchema.parse({
        ...packCategory,
    });
};

/**
 * Validates pack category form values.
 * Logs any validation errors to the console.
 *
 * @param {IPackCategoryFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validatePackCategoryForm = (formValues : IPackCategoryFormValues) : boolean => {
    try {
        packCategoryFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Pack category form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
